import type { RadioChangeEvent } from 'antd/lib';
import type { MeetingFormData, MeetingPayloadData } from '../types';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Typography, Form, Divider, Radio, Tooltip, Spin } from 'antd';
import dayjs from 'dayjs';
import { InfoCircleOutlined } from '@ant-design/icons';

import themeToken from '@lib/theme/tokens/index';
import { StyledInput } from '@/lib/theme/components/Input';
import { StyledButton } from '@/lib/theme/components/Button';
import { StyledDatePicker } from '@/lib/theme/components/DatePicker';
import { StyledSelect } from '@/lib/theme/components/Select';
import { StyledForm } from '@/components/Shared/Form';
import { useSelectedCompanyStore } from '@/stores/selectedCompany';
import CustomizeRequiredMark from '@/components/Shared/CustomizeRequiredMark';
import { useTrackEvent } from '@/hooks/useTrackEvents';
import { useGetUsers } from '@/features/usersManagement/api/Users/getUsers';
import CommonModal from '@/features/captable/components/Shared/CommonModal';

import { useGetMeetingTypes } from '../api/meetings/getMeetingTypes';
import MeetingTypeForm from './MeetingTypeForm';
import { useAddMeeting } from '../api/meetings/addMeeting';
import { useGetMeetingDetails } from '../api/meetings/getMeetingDetails';
import { useEditMeeting } from '../api/meetings/editMeeting';
import { AttendeesInclusion } from '../utils/enums';
import MultipleTextInputs from '@/components/Shared/MultipleTextInputs';

const { Title, Text } = Typography;

type Props = {
  handleCancel?: () => void;
  handleOk?: (equityID?: number) => void;
  selectedMeetingId?: number;
};

const MeetingForm = ({ handleCancel, handleOk, selectedMeetingId }: Props) => {
  const { sendMixpanelEvent } = useTrackEvent({ page: selectedMeetingId ? 'edit-meeting-modal' : 'add-meeting-modal' });
  const { t } = useTranslation('investorRelations', {
    keyPrefix: 'meetings',
  });

  const { selectedCompany } = useSelectedCompanyStore((state) => state);

  const [form] = Form.useForm();
  const [meetingGuests, setMeetingGuests] = useState<string[]>([]);
  const users_inclusion = Form.useWatch<AttendeesInclusion>('users_inclusion', form);

  const { mutate: mutateAddMeeting, isPending: isAddMeetingPending } = useAddMeeting({
    postSuccess: () => {
      handleOk?.();
    },
    postError: (message, body) => {
      if (
        body.users_inclusion === AttendeesInclusion.OWNERSHIP &&
        form.getFieldValue('users_inclusion') === AttendeesInclusion.OWNERSHIP &&
        form.getFieldValue('min_ownership') === body.min_ownership &&
        /no shareholders|min_ownership/i.test(message)
      ) {
        form.setFields([{ name: 'min_ownership', errors: [message] }]);
        return true;
      }
      return false;
    },
  });

  const { mutate: mutateEditMeeting, isPending: isEditMeetingPending } = useEditMeeting({
    postSuccess: () => {
      handleOk?.();
    },
  });

  const { data: meetingDetails, isLoading: isGetMeetingDetailsLoading } = useGetMeetingDetails({
    companyID: selectedCompany?.cid as number,
    meetingID: selectedMeetingId as number,
  });

  // Meeting Types Pagination
  const [meetingTypesPage, setMeetingTypesPage] = useState(1);
  const [meetingTypesSearchQuery, setMeetingTypesSearchQuery] = useState<string>();
  const { data: meetingTypes, isLoading: isGetMeetingTypesLoading } = useGetMeetingTypes({
    companyID: selectedCompany?.cid as number,
    queryParams: meetingTypesSearchQuery
      ? {
          page: meetingTypesPage,
          q: meetingTypesSearchQuery,
        }
      : { page: meetingTypesPage },
  });

  // Users Options Pagination with API query params
  const [usersOptionsPaginationParams, setUsersOptionsPaginationParams] = useState({
    pageSize: 10,
    current: 1,
    total: 0,
  });
  const [usersOptions, setUsersOptions] = useState<{ value: number; label: string }[]>([]);
  const {
    current: usersOptionsCurrentPage,
    pageSize: usersOptionsPageSize,
    total: usersOptionsTotal,
  } = usersOptionsPaginationParams;

  const { data: usersListOptions, isFetching: isGetUsersListOptionsFetching } = useGetUsers({
    companyID: selectedCompany?.cid as number,
    queryParams: {
      page: usersOptionsCurrentPage,
      limit: usersOptionsPageSize,
    },
  });

  const [isMeetingTypeModalOpen, setIsMeetingTypeModalOpen] = useState<boolean>(false);

  const AttendeesInclusionOptions = useMemo(() => {
    return [
      { label: t('all'), value: AttendeesInclusion.ALL },
      { label: t('include'), value: AttendeesInclusion.INCLUDE },
      { label: t('exclude'), value: AttendeesInclusion.EXCLUDE },
      { label: t('ownershipInclusion'), value: AttendeesInclusion.OWNERSHIP },
    ];
  }, [t]);

  const handleMeetingTypeModalOpen = useCallback(() => {
    sendMixpanelEvent('add-new-meeting-type-btn-clicked');
    setIsMeetingTypeModalOpen(true);
  }, [setIsMeetingTypeModalOpen, sendMixpanelEvent]);

  const handleMeetingTypeModalCancel = useCallback(() => {
    sendMixpanelEvent('cancel-meeting-type-btn-clicked');
    setIsMeetingTypeModalOpen(false);
  }, [setIsMeetingTypeModalOpen, sendMixpanelEvent]);

  const handleMeetingTypeModalFinish = useCallback(() => {
    sendMixpanelEvent('save-meeting-type-btn-clicked');
    setIsMeetingTypeModalOpen(false);
  }, [setIsMeetingTypeModalOpen, sendMixpanelEvent]);

  const handleInclusionChange = useCallback(
    (event: RadioChangeEvent) => {
      if (event.target.value === AttendeesInclusion.ALL || event.target.value === AttendeesInclusion.OWNERSHIP) {
        form.setFieldsValue({ company_user_ids: [] });
      }
      form.setFields([{ name: 'min_ownership', value: undefined, errors: [] }]);
    },
    [form],
  );

  const canLoadMoreUsersOptions = useMemo(
    () => usersOptionsCurrentPage * usersOptionsPageSize < usersOptionsTotal,
    [usersOptionsCurrentPage, usersOptionsPageSize, usersOptionsTotal],
  );

  const handleUsersOptionsPopupScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 24;
      if (isNearBottom && canLoadMoreUsersOptions && !isGetUsersListOptionsFetching) {
        setUsersOptionsPaginationParams((prev) => ({ ...prev, current: prev.current + 1 }));
      }
    },
    [canLoadMoreUsersOptions, isGetUsersListOptionsFetching],
  );

  const cancelClickHandler = () => {
    sendMixpanelEvent('back-btn-clicked');
    form.resetFields();
    handleCancel?.();
  };

  const onFinish = (values: MeetingFormData) => {
    const { meeting_date, meeting_time, voting_start_date, voting_start_time, min_ownership, ...rest } = values;
    sendMixpanelEvent('submit-btn-clicked');
    const start_time = dayjs(meeting_date)
      .set('hour', dayjs(meeting_time).hour())
      .set('minute', dayjs(meeting_time).minute())
      .toISOString();
    const vote_start_date = dayjs(voting_start_date)
      .set('hour', dayjs(voting_start_time).hour())
      .set('minute', dayjs(voting_start_time).minute())
      .toISOString();

    const body: MeetingPayloadData = {
      ...rest,
      start_time,
      vote_start_date,
      company_user_ids:
        rest.users_inclusion === AttendeesInclusion.OWNERSHIP || rest.users_inclusion === AttendeesInclusion.ALL
          ? []
          : rest.company_user_ids ?? [],
    };

    body.location = body.location || undefined;
    body.meeting_link = body.meeting_link || undefined;

    if (selectedMeetingId) {
      const { company_user_ids, ...editBody } = body;

      mutateEditMeeting({
        companyID: selectedCompany?.cid as number,
        meetingID: selectedMeetingId,
        body: editBody,
      });
    } else {
      if (body.users_inclusion === AttendeesInclusion.OWNERSHIP) {
        body.min_ownership = min_ownership;
      }
      body.meeting_guest_emails = meetingGuests.length ? meetingGuests : undefined;
      mutateAddMeeting({
        companyID: selectedCompany?.cid as number,
        body,
      });
    }
  };

  useEffect(() => {
    if (meetingDetails) {
      const { data } = meetingDetails;
      const meeting_date = dayjs(data.start_time);
      const meeting_time = dayjs(data.start_time);
      const company_user_ids = data.meeting_attendees.map((attendee) => attendee.company_user.uid);

      form.setFieldsValue({
        ...data,
        meeting_date,
        meeting_time,
        company_user_ids,
      });
    } else {
      // form initialization
      form.setFieldsValue({
        users_inclusion: AttendeesInclusion.INCLUDE,
      });
    }
  }, [meetingDetails, form]);

  useEffect(() => {
    setUsersOptions([]);
    setUsersOptionsPaginationParams((prev) => ({ ...prev, current: 1, total: 0 }));
  }, [selectedCompany?.cid]);

  useEffect(() => {
    if (!usersListOptions) return;

    const { metadata, data } = usersListOptions;
    setUsersOptionsPaginationParams((prev) => ({ ...prev, total: metadata.total }));

    const mappedOptions = data.map((user) => ({
      value: user.id,
      label: user.name + ` (${user.mail})`,
    }));

    setUsersOptions((prev) => {
      if (usersOptionsCurrentPage === 1) {
        return mappedOptions;
      }
      const existingIds = new Set(prev.map((option) => option.value));
      const nextOptions = mappedOptions.filter((option) => !existingIds.has(option.value));
      return [...prev, ...nextOptions];
    });
  }, [usersListOptions, usersOptionsCurrentPage]);

  return (
    <>
      <StyledForm
        layout="vertical"
        form={form}
        onFinish={onFinish}
        size="large"
        requiredMark={CustomizeRequiredMark}
        disabled={isGetMeetingDetailsLoading || isGetMeetingTypesLoading || isGetUsersListOptionsFetching}
      >
        <Flex align="start" vertical>
          <Title
            level={5}
            style={{
              color: themeToken['branding-primary-6'],
            }}
          >
            {selectedMeetingId ? t('editMeeting') : t('newMeeting')}
          </Title>
          <Divider
            style={{
              marginBlock: themeToken.margin,
              borderColor: themeToken['branding-natural-3'],
            }}
          />

          <Flex gap={24} style={{ width: '100%' }} justify="space-between">
            <Form.Item
              label={t('meetingName')}
              name="name"
              rules={[{ required: true, message: 'Please enter meeting name' }]}
            >
              <StyledInput placeholder={t('enterName')} />
            </Form.Item>
            <Form.Item
              label={t('meetingType')}
              name="meeting_type_id"
              rules={[{ required: true, message: 'Please select meeting type' }]}
              style={{
                marginBlock: 0,
              }}
            >
              <StyledSelect
                showSearch
                loading={isGetMeetingTypesLoading}
                onPopupScroll={(event: React.UIEvent<HTMLElement>) => {
                  const target = event.currentTarget;
                  if (!isGetMeetingTypesLoading && target.scrollTop + target.offsetHeight === target.scrollHeight) {
                    setMeetingTypesPage(meetingTypesPage + 1);
                    target.scrollTo(0, target.scrollHeight);
                  }
                }}
                options={
                  meetingTypes?.data?.map((type) => ({
                    label: type.name,
                    value: type.id,
                  })) || []
                }
                onSearch={(value: string) => {
                  setMeetingTypesSearchQuery(value);
                  setMeetingTypesPage(1);
                }}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                placeholder={t('meetingType')}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    {isGetMeetingTypesLoading && (
                      <Flex justify="center" style={{ padding: '8px 0' }}>
                        <Spin size="small" />
                      </Flex>
                    )}
                    <Divider style={{ margin: '8px 0' }} />
                    <StyledButton
                      onClick={handleMeetingTypeModalOpen}
                      type="primary"
                      ghost
                      size="small"
                      style={{
                        marginInlineStart: 'auto',
                      }}
                    >
                      {t('addNewType')}
                    </StyledButton>
                  </>
                )}
              />
            </Form.Item>
          </Flex>

          <Flex gap={24} style={{ width: '100%' }} justify="space-between">
            <Form.Item
              label={t('meetingDate')}
              name="meeting_date"
              rules={[{ required: true, message: 'Please select meeting date' }]}
            >
              <StyledDatePicker
                placeholder={t('addMeetingDate')}
                style={{
                  height: themeToken.controlHeightLG,
                }}
              />
            </Form.Item>
            <Form.Item
              label={t('meetingTime')}
              name="meeting_time"
              rules={[{ required: true, message: 'Please select meeting time' }]}
            >
              <StyledDatePicker
                placeholder={t('addMeetingTime')}
                picker="time"
                use12Hours
                format={'hh:mm A'}
                style={{
                  height: themeToken.controlHeightLG,
                }}
              />
            </Form.Item>
          </Flex>
          <Flex gap={24} style={{ width: '100%' }} justify="space-between">
            <Form.Item
              label={t('votingStartDate')}
              name="voting_start_date"
              rules={[{ required: true, message: 'Please select voting start date' }]}
            >
              <StyledDatePicker
                placeholder={t('addVotingStartDate')}
                style={{
                  height: themeToken.controlHeightLG,
                }}
              />
            </Form.Item>
            <Form.Item
              label={t('votingStartTime')}
              name="voting_start_time"
              rules={[{ required: true, message: 'Please select voting start time' }]}
            >
              <StyledDatePicker
                placeholder={t('addVotingStartTime')}
                picker="time"
                use12Hours
                format={'hh:mm A'}
                style={{
                  height: themeToken.controlHeightLG,
                }}
              />
            </Form.Item>
          </Flex>

          <Flex gap={24} style={{ width: '100%' }} justify="space-between">
            <Form.Item label={t('location')} name="location">
              <StyledInput placeholder={t('locationPlaceholder')} />
            </Form.Item>
            <Form.Item
              label={t('conferenceLink')}
              name="meeting_link"
              rules={[
                {
                  pattern: /^https?:\/\/.+\..+/,
                  message: t('validConferenceLinkError'),
                },
              ]}
            >
              <StyledInput placeholder={t('conferenceLinkPlaceholder')} />
            </Form.Item>
          </Flex>

          {!selectedMeetingId && (
            <>
              <Form.Item
                label={t('attendeesInclusion')}
                name="users_inclusion"
                rules={[{ required: true, message: 'Please select attendees inclusion' }]}
              >
                <Radio.Group
                  options={AttendeesInclusionOptions}
                  optionType="button"
                  buttonStyle="solid"
                  onChange={handleInclusionChange}
                />
              </Form.Item>
              {users_inclusion === AttendeesInclusion.OWNERSHIP ? (
                <Form.Item
                  label={t('minimumOwnership')}
                  name="min_ownership"
                  preserve={false}
                  extra={t('minimumOwnershipHelp')}
                  getValueFromEvent={(event: React.ChangeEvent<HTMLInputElement>) =>
                    event.target.value === '' ? undefined : event.target.valueAsNumber
                  }
                  rules={[
                    { required: true, message: t('minimumOwnershipRequired') },
                    { type: 'number', min: 0, max: 100, message: t('minimumOwnershipInvalid') },
                  ]}
                >
                  <StyledInput
                    type="number"
                    min={0}
                    max={100}
                    step="any"
                    inputMode="decimal"
                    placeholder={t('minimumOwnership')}
                    onKeyDown={(event) => {
                      if (!event.ctrlKey && !event.metaKey && ['-', '+', 'e', 'E'].includes(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    onPaste={(event) => {
                      if (!/^(?:\d+\.?\d*|\.\d+)$/.test(event.clipboardData.getData('text'))) event.preventDefault();
                    }}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  label={t('addAttendee')}
                  name="company_user_ids"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  rules={[{ required: users_inclusion !== AttendeesInclusion.ALL, message: 'Please select attendees' }]}
                  style={{
                    marginBlock: 0,
                  }}
                >
                  <StyledSelect
                    showSearch
                    mode="multiple"
                    loading={isGetUsersListOptionsFetching}
                    onPopupScroll={handleUsersOptionsPopupScroll}
                    options={usersOptions}
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    placeholder={t('addAttendee')}
                    disabled={selectedMeetingId ? true : users_inclusion === AttendeesInclusion.ALL}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        {isGetUsersListOptionsFetching && (
                          <Flex justify="center" style={{ padding: '8px 0' }}>
                            <Spin size="small" />
                          </Flex>
                        )}
                      </>
                    )}
                  />
                </Form.Item>
              )}
              <Flex vertical gap={8} style={{ width: '100%', marginBottom: 24 }}>
                <Flex>
                  <Text>{t('meetingGuests')}</Text>
                  <Tooltip title={t('meetingGuestsTooltip')}>
                    <InfoCircleOutlined style={{ marginLeft: 8 }} />
                  </Tooltip>
                </Flex>
                <MultipleTextInputs setTexts={setMeetingGuests} texts={meetingGuests} subText={t('guests')} />
              </Flex>
            </>
          )}
          <Divider
            style={{
              marginBlock: themeToken.margin,
              borderColor: themeToken['branding-natural-3'],
              marginBlockStart: 'auto',
            }}
          />
          <Flex style={{ width: '100%' }} justify="space-between">
            <StyledButton ghost type="primary" onClick={cancelClickHandler} size="middle">
              {t('back')}
            </StyledButton>
            <StyledButton
              loading={
                isGetMeetingTypesLoading || isGetMeetingDetailsLoading || isAddMeetingPending || isEditMeetingPending
              }
              type="primary"
              htmlType="submit"
              size="middle"
            >
              {t('save')}
            </StyledButton>
          </Flex>
        </Flex>
      </StyledForm>
      <CommonModal isModalOpen={isMeetingTypeModalOpen} handleCancel={handleMeetingTypeModalCancel}>
        <MeetingTypeForm handleCancel={handleMeetingTypeModalCancel} handleOk={handleMeetingTypeModalFinish} />
      </CommonModal>
    </>
  );
};

export default MeetingForm;
